import { Card, CardContent, CardHeader, CardTitle } from '@predoc/cow';
import { ArrowDown } from 'lucide-react';
import { DocumentTemplateShell } from './TemplateShared';
import {
  TemplateAppendixLayout,
  TemplateSummaryHeader,
} from './TemplateAppendixLayout';
import { TemplatePractitionerBlock } from './TemplatePractitionerBlock';
import {
  DASH,
  EMPTY,
  NOT_AVAILABLE,
  formatLongDate,
  formatTimestamp,
} from './templateUtils';
import type { TemplatePatient, TemplatePractitioner } from './templateUtils';

export type AllergiesTemplatePractitioner = TemplatePractitioner;

export interface AllergyEntry {
  id?: string;
  name?: string;
  reaction?: string;
  recordedDate?: string;
  onsetStart?: string;
  onsetEnd?: string;
  practitioner?: AllergiesTemplatePractitioner;
}

export interface AllergiesTemplateProps {
  entries: AllergyEntry[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightEntryId?: string;
}

const summaryHeaders = [
  'Name',
  'Onset Start Date',
  'Reaction',
  'Page',
] as const;

const SummaryTable = ({ entries }: { entries: AllergyEntry[] }) => (
  <div className="overflow-hidden rounded-sm border border-zinc-200">
    <div className="flex bg-slate-100">
      {summaryHeaders.map((h, idx) => (
        <div
          key={h}
          className={`flex flex-1 px-3 py-2.5 ${
            idx < summaryHeaders.length - 1 ? 'border-r border-zinc-300' : ''
          }`}
        >
          <p className="flex-1 text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
            {h}
          </p>
        </div>
      ))}
    </div>
    {entries.map((entry, idx) => {
      const zebra = idx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
      const entryName = entry.name?.trim() || 'this allergy';
      return (
        <div
          key={entry.id ?? `al-${idx}`}
          className={`flex border-t border-zinc-200 ${zebra}`}
        >
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2.5">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {entry.name?.trim() || DASH}
            </p>
          </div>
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2.5">
            <p className="flex-1 text-[10px] leading-tight text-zinc-950">
              {formatLongDate(entry.onsetStart)}
            </p>
          </div>
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2.5">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {entry.reaction?.trim() || DASH}
            </p>
          </div>
          <div className="flex flex-1 px-3 py-2.5">
            <a
              href={`#allergy-${entry.id}`}
              aria-label={`Jump to allergy details for ${entryName}`}
              className="flex flex-1 items-center justify-center text-zinc-950"
            >
              <ArrowDown aria-hidden="true" className="size-4" />
            </a>
          </div>
        </div>
      );
    })}
  </div>
);

const AppendixCard = ({
  entry,
  highlightEntryId,
}: {
  entry: AllergyEntry;
  highlightEntryId?: string;
}) => {
  const isHighlighted = !!highlightEntryId && entry.id === highlightEntryId;
  return (
    <Card
      id={`allergy-${entry.id}`}
      tabIndex={isHighlighted ? -1 : undefined}
      className={`flex flex-col overflow-hidden rounded-sm border-2 ${
        isHighlighted ? 'border-amber-400' : 'border-gray-200'
      } bg-white p-0 pb-4 shadow-none`}
    >
      <CardHeader className="flex flex-col gap-1.5 rounded-none bg-slate-700 px-5 pt-5 pb-3 text-white">
        <CardTitle className="text-[14px] leading-tight font-semibold text-white">
          {entry.name?.trim() || EMPTY}
        </CardTitle>
        <p className="text-[10px] leading-tight">
          <span className="font-semibold">Recorded:</span>{' '}
          {formatTimestamp(entry.recordedDate)}{' '}
          <span className="opacity-70">|</span>{' '}
          <span className="font-semibold">Onset Start:</span>{' '}
          {formatTimestamp(entry.onsetStart)}{' '}
          <span className="opacity-70">|</span>{' '}
          <span className="font-semibold">Onset End:</span>{' '}
          {formatTimestamp(entry.onsetEnd)}
        </p>
      </CardHeader>

      <CardContent className="mx-5 mt-3 flex flex-col gap-3 rounded-sm border border-zinc-200 bg-white px-4 py-3">
        <div className="flex flex-col gap-1">
          <p className="text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
            Reaction
          </p>
          <p className="text-[11px] leading-tight whitespace-pre-line text-zinc-950">
            {entry.reaction?.trim() || NOT_AVAILABLE}
          </p>
        </div>

        <div className="h-px w-full bg-zinc-200" />

        <TemplatePractitionerBlock practitioner={entry.practitioner} />
      </CardContent>
    </Card>
  );
};

export const AllergiesTemplate = ({
  entries,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightEntryId,
}: AllergiesTemplateProps) => {
  const items = entries ?? [];
  const hasItems = items.length > 0;

  return (
    <DocumentTemplateShell
      title="Allergies Report"
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
    >
      <TemplateAppendixLayout
        hasItems={hasItems}
        emptyMessage="No allergies available"
        summaryHeader={
          <TemplateSummaryHeader
            title="All Reported Allergies"
            subtitle="Sorted by onset start date (newest first)"
            note="*Refer to appendix below for more details"
          />
        }
        summaryTable={<SummaryTable entries={items} />}
        appendix={items.map((entry) => (
          <AppendixCard
            key={entry.id}
            entry={entry}
            highlightEntryId={highlightEntryId}
          />
        ))}
      />
    </DocumentTemplateShell>
  );
};
